import React, { useEffect, useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { createLinkToken, connectBankAccount } from '../../services/plaid.service';
import { usePlaidLink } from 'react-plaid-link';
import ConfirmationModal from '../components/confirmation.modal.component';
import { useAuth } from '../components/autentication-context.component';
import { useLocation, useNavigate } from 'react-router-dom';
import AccountHeader from '../components/account-header.component';
import AccountList from '../components/account-list.component';
import AddAccountButton from '../components/add-account.button.component';
import Notification from '../components/notification.component';
import { deleteAccount, getBankAccounts, buildEarlyRequestTransaction } from '../../services/account.service';
import { BankAccount } from '../interfaces/bank-account.interface';
import { TransactionStatus } from '../../enum/transaction-status.enum';
import { SpinnerComponent } from '../components/spinner.component';
import { LogoutProps } from '../interfaces/logout.interface';
import { handleApiError } from '../../services/error.service';

const DestinationAccounts: React.FC<LogoutProps> = ({ handleLogout }) => {
  const { employeeId, token, updateToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { amountToDeposit } = location.state || {};

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const dummyToken = 'dummy-token';

  const fetchLinkTokenAndAccounts = useCallback(async () => {
    if (!employeeId || !token) return;
    setLoading(true);
    try {
      const { link_token } = await createLinkToken(token, updateToken);
      setLinkToken(link_token);
      const accountsData = await getBankAccounts(employeeId, token, updateToken);
      setAccounts(accountsData);
    } catch (error) {
      handleApiError({ error, handleLogout, setNotification });
    } finally {
      setLoading(false);
    }
  }, [employeeId, token, updateToken, handleLogout]);

  useEffect(() => {
    fetchLinkTokenAndAccounts();
  }, [fetchLinkTokenAndAccounts]);

  const { open, ready } = usePlaidLink({
    token: linkToken || dummyToken,
    onSuccess: async (public_token) => {
      try {
        await connectBankAccount(public_token, employeeId!, token!, updateToken);
        await fetchLinkTokenAndAccounts();
        setNotification({ type: 'success', message: 'Bank account added successfully!' });
      } catch (error) {
        handleApiError({ error, handleLogout, setNotification });
      }
    },
  });

  const handleDeleteAccount = useCallback(async () => {
    if (selectedAccountId === null) {
      setNotification({ type: 'error', message: 'No account selected for deletion' });
      return;
    }
    setShowModal(false);
    setLoading(true);

    try {
      const resp = await deleteAccount(selectedAccountId, token!, updateToken);
      if (!resp?.ok) {
        setNotification({ type: 'error', message: resp?.message || 'Bank account could not be deleted' });
        return;
      }
      setNotification({ type: 'success', message: resp.message });
      setAccounts((prev) => prev.filter((account) => account.id !== selectedAccountId));
    } catch (error) {
      handleApiError({ error, handleLogout, setNotification });
    } finally {
      setSelectedAccountId(null);
      setLoading(false);
    }
  }, [selectedAccountId, token, updateToken, handleLogout]);

  const confirmDeleteAccount = (id: number) => {
    setSelectedAccountId(id);
    setShowModal(true);
  };

  const handleDeposit = useCallback(async (accountId: number, bankName: string, accountNumber: string, accountRoutingNumber: string) => {
    if (!amountToDeposit) return;
    setLoading(true);

    try {
      const response = await buildEarlyRequestTransaction(amountToDeposit, accountId, bankName, accountNumber, accountRoutingNumber);
      navigate('/transaction-confirmation', {
        state: response || {
          requestedAmount: `$${amountToDeposit.toFixed(2)}`,
          date: new Date().toLocaleString(),
          bankName: 'N/A',
          accountId: 'N/A',
          accountNumber: 'N/A',
          accountRoutingNumber: 'N/A'
        }
      });
    } catch (error) {
      navigate('/transaction-details', {
        state: {
          Status: TransactionStatus.Failed,
          Transaction: 'N/A',
          Detail: {
            RequestedAmount: `$${amountToDeposit.toFixed(2)}`,
            Date: new Date().toLocaleString(),
          },
          DestinationAccount: {
            BankAccount: 'N/A',
            BankAccountNumber: 'N/A',
          },
        },
      });
    } finally {
      setLoading(false);
    }
  }, [amountToDeposit, navigate]);

  return (
    <Container className="mt-5">
      {notification && (
        <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}
      <AccountHeader amountToDeposit={amountToDeposit} />
      <AddAccountButton open={open as () => void} ready={ready} linkToken={linkToken} />
      {loading ? (
        <SpinnerComponent />
      ) : (
        <AccountList
          accounts={accounts}
          amountToDeposit={amountToDeposit}
          onDeposit={handleDeposit}
          confirmDeleteAccount={confirmDeleteAccount}
        />
      )}
      <ConfirmationModal
        show={showModal}
        message="Are you sure you want to delete this account?"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowModal(false)}
      />
    </Container>
  );
};

export default DestinationAccounts;